/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard (C) Copyright IBM Corp. 2013, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'./PageCollectionView',
	'jquery',
	'underscore',
	'react',
	'react-dom',
	'classnames',
	'corsair_ship/ba-react-html5-dnd',
	'@ba/ba-ui-carbon-toolkit',
	'../../util/ColorUtils',
	'../../../app/nls/StringResources',
	'../../../lib/@waca/dashboard-common/dist/ui/dialogs/ConfirmationDialog',
	'../../../app/util/EventChainLocal',
	'../../../lib/@waca/core-client/js/core-client/utils/BidiUtil',
	'../../../lib/@waca/dashboard-common/dist/utils/TextEditorConstants',
	'../../../features/content/mobileResponsiveLayout/ResponsiveMenuController'
], function(
	BaseLayout,
	$,
	_,
	React,
	ReactDOM,
	cx,
	ReactHtml5DnDContext,
	UI_Toolkit,
	ColorUtils,
	StringResources,
	ConfirmationDialog,
	EventChainLocal,
	BidiUtil,
	TextEditorConstants,
	ResponsiveMenuController
) {
	/* global MutationObserver */

	const linePositionMap = {
		top: 'bottom',
		left: 'right',
		bottom: 'top',
		right: 'left'
	};

	const colorPropertiesMapping = [{
		modelPropertyName: 'tabTextColor',
		toolkitPropertyName: 'textColor'
	}, {
		modelPropertyName: 'tabSelectedLineColor',
		toolkitPropertyName: 'lineColor'
	}, {
		modelPropertyName: 'tabBackgroundColor',
		toolkitPropertyName: 'backgroundColor'
	}, {
		modelPropertyName: 'tabIconColor',
		toolkitPropertyName: 'iconColor'
	}];

	const Tab = BaseLayout.extend({

		init: function(options) {
			Tab.inherited('init', this, arguments);

			this.eventRouter = options.eventRouter;
			this.canvas = options.canvas;
			this.colorsService = this.dashboardApi.getFeature('Colors');
			this._icons = this.dashboardApi.getFeature('Icons');
			this._dashboardState = this.dashboardApi.getFeature('DashboardState');

			this._registerEvents();

			this._initialContentTab = this._initializeTabContent();

			this._setTabsVisibility();
			this._TabsWidget = null;

			Object.defineProperty(this, 'tabWidget', {
				get: function() {
					return this._TabsWidget;
				},
				configurable: true
			});

			this._delayedRendering = {};
			this._renderingMountingNode = {};
			this._mountedNodesArePopulated = {};
		},

		/**
		 * Obtain the current tab level selected content.
		 * If the tab level content is not selected, default to the first tab content
		 * @return selected tab level content
		 */
		_initializeTabContent: function() {
			if (!this.model.getParent()) {
				const tabContents = this.canvas.getContent().getChildren();
				const selected = this.canvas.getSelectedContentList({ type: tabContents[0].getType() });
				this.selectedModel = this.model.items.find(item => item.id === (selected.length && selected[0].id || tabContents[0].getId()));
				return selected.length ? selected[0] : tabContents[0];
			} else {
				//tab widget..
				return this.content.getChildren()[0];
			}
		},

		renderContent: function(){
			if (!this.rendered) {
				return Tab.inherited('renderContent', this, arguments).then(() => {
					const initialTabContentId = this._initialContentTab && this._initialContentTab.getId();
					this._renderTabs(initialTabContentId);
					this._renderResponsiveMenu();
					this.showView(this.model.findModel(initialTabContentId));
					this.rendered = true;
				});

			} else {
				return Promise.resolve(false);
			}
		},

		_renderResponsiveMenu() {
			const renderer = this.dashboardApi.getFeature('Renderer.internal');
			// !this.model.getParent() is to exclude tab widgets
			if (renderer && renderer.isMobile() && !this.model.getParent()) {
				this._menuController = new ResponsiveMenuController({ content: this.content, features: { 'Canvas': this.canvas, 'Icons': this._icons } });
				this._menuController.setContainer(this.domNode);
				this._menuController.renderResponsiveMenuUI();
			}
		},

		_getTabNode(el, id) {
			let pageTabContainer = el.parentElement;

			if (!pageTabContainer) {
				pageTabContainer = document.createElement('div');
				pageTabContainer.id = `${id}_tab`;
				pageTabContainer.classList.add('pageTabContent');
				pageTabContainer.append(el);
			}

			// Make sure to mark the pageTabContent if selected
			if (this.selectedModel && this.selectedModel.id === id) {
				pageTabContainer.classList.add('selected');
			} else {
				pageTabContainer.classList.remove('selected');
			}
			return pageTabContainer;
		},

		renderChild(id, forceRenderForNonSelectedTabs /* forceRenderForNonSelectedTabs is used in the layoutController for the printing usecase where we want to force all tabs to render */) {
			const initialTabContentId = this._initialContentTab && this._initialContentTab.getId();
			if (id === initialTabContentId || (this.selectedModel && id === this.selectedModel.id) || forceRenderForNonSelectedTabs){
				if (this._delayedRendering[id]) {
					delete this._delayedRendering[id];
				}
				return Tab.inherited('renderChild', this, arguments).then(() => {
					if (forceRenderForNonSelectedTabs){
						return this.layoutController.getLayoutViewWhenReady(id).then((layoutView) => {
							// if the page node is still not attach to the dom, let us add it to page holder
							if (layoutView && !layoutView.domNode.parentElement) {
								const pageContent = this._getTabNode(layoutView.domNode, id);
								const children = this.domNode.querySelectorAll('.tabPageHolder');
								if (children.length) {
									children.forEach(child => child.append(pageContent));
								}
							}
						});
					}
				});
			} else {
				this._delayedRendering[id] = true;
				return Promise.resolve();
			}
		},

		destroy: function() {
			if (this._menuController){
				this._menuController.destroy();
				this._menuController = null;
			}
			this._unregisterEvents();
			this.selectedModel = null;
			delete this.tabWidget;
			ReactDOM.unmountComponentAtNode(this.$el.find('.tabCntr')[0]);
			$('.tabLabel',this.$el).remove();

			Tab.inherited('destroy', this, arguments);
			this._TabsWidget = null;
			this._delayedRendering = null;
			this._renderingMountingNode = null;
			this._mountedNodesArePopulated = null;
		},

		_registerEvents: function () {
			if (this.colorsService) {
				this.colorsService.on('theme:changed', this.applyTheme, this);
				this.colorsService.on('colorSet:changed', this.applyTheme, this);
			}
			if (this.model) {
				this.model.on('change:tabTextColor', this.applyTheme, this);
				this.model.on('change:tabSelectedLineColor', this.applyTheme, this);
				this.model.on('change:tabBackgroundColor', this.applyTheme, this);
				this.content.on('add:child', this.onTabAdded, this);
				this.content.on('remove:child', this.onTabRemoved, this);
				this.model.on('change:hideTab', this.updateHideTabState, this);
				this.model.on('change:fillColor', this.applyTheme, this);
				this.model.on('change:tabPosition', this.updateTabPosition, this);
				this.model.on('change:tabIconPosition', this.updateTabIconPosition, this);
				this.model.on('change:showDropDown', this._updateProperties, this);
				this.model.on('change:tabFontFamily', this._updateFontFamily, this);
				this.model.on('change:tabFontFamilySubClass', this._updateFontFamily, this);
			}
			if (this.eventRouter) {
				this.eventRouter.on('properties:hasRendered', this.setInitialEnabledState, this);
			}

			this.canvas.on('change:selections:select', this.onSelection, this);
			// TODO: We should modify dashboardstate/callbackinvoker to allow it accept context/'this'
			// so that each consumer doesn't have to maintain the bound function
			this._boundSetTabsVisibility = this._setTabsVisibility.bind(this);
			this._dashboardState.onUiStateChange(this._boundSetTabsVisibility);

			this.model.on('change:showDropDown', this._updateProperties, this);
		},

		_unregisterEvents: function () {
			if (this.colorsService) {
				this.colorsService.off('theme:changed', this.applyTheme, this);
				this.colorsService.off('colorSet:changed', this.applyTheme, this);
			}

			if (this.model) {
				this.model.off('change:tabTextColor', this.applyTheme, this);
				this.model.off('change:tabSelectedLineColor', this.applyTheme, this);
				this.model.off('change:tabBackgroundColor', this.applyTheme, this);
				this.content.off('add:child', this.onTabAdded, this);
				this.content.off('remove:child', this.onTabRemoved, this);
				this.model.off('change:hideTab', this.updateHideTabState, this);
				this.model.off('change:fillColor', this.applyTheme, this);
				this.model.off('change:tabPosition', this.updateTabPosition, this);
				this.model.off('change:tabIconPosition', this.updateTabIconPosition, this);
				this.model.off('change:showDropDown', this._updateProperties, this);
				this.model.off('change:tabFontFamily', this._updateFontFamily, this);
				this.model.off('change:tabFontFamilySubClass', this._updateFontFamily, this);
			}
			if (this.eventRouter) {
				this.eventRouter.off('properties:hasRendered', this.setInitialEnabledState, this);
			}

			this.canvas.off('change:selections:select', this.onSelection, this);
			this._dashboardState.offUiStateChange(this._boundSetTabsVisibility);
		},

		/**
		 * Overrides the method from LayoutBaseView so we can also call "applyTheme" when in a dashboard with tabs.
		 * @override
		 */
		_onChangeBackgroundColor: function() {
			if (this._TabsWidget && Tab.inherited('_onChangeBackgroundColor', this, arguments)) {
				this.applyTheme();
			}
		},

		_getPanelId: function(id) {
			return id + '_tabpanel';
		},

		_getModelIdFromPanel: function(id) {
			return id.slice(0, -9);
		},

		_getDefaultLinePostion: function(tabPosition = 'top') {
			return linePositionMap[tabPosition];
		},

		_getDashboardColorProperties: function () {
			const properties = {};

			colorPropertiesMapping.forEach((property) => {
				_.extend(properties, this._getColorProperty(property));
			});

			return properties;
		},

		_getColorProperty: function (property, model = this.model, tabContent = null) {
			const name = property.modelPropertyName;
			const properties = {};

			// The order (of most to least important) we care about for a background colour being set is:
			// 1. tab model tabBackgroundColor (overrides everything)
			// 2. layout model tabBackgroundColor probably (background of tab button not canvas) (overrides tab canvas background)
			// 3. tab canvas background (used if all others are not set/transparent).

			let tabContentBgProperty;
			if (tabContent && this.selectedModel) {
				const indivTabBgProperty = this.content.getContent(this.selectedModel.id).getPropertyValue('backgroundColor');
				if (indivTabBgProperty && indivTabBgProperty !== 'transparent') {
					tabContentBgProperty = indivTabBgProperty;
				} else {
					// Note: model.get('fillColor') is for TabWidget tabs and NOT for dashboard canvas tabs
					tabContentBgProperty = model.get('fillColor') || this.model.get('fillColor');
				}
			}

			const bgColorId = model.get('tabBackgroundColor') || this.model.get('tabBackgroundColor') || tabContentBgProperty;
			const themeColors = this.colorsService.getForegroundColorPropertiesForUIElement(bgColorId, 'tabs');

			let hexColor;
			if (model.get(name)) {
				hexColor = this.colorsService.getHexColorFromDashboardColorSet(model.get(name));
			}

			if(!hexColor && name === 'tabBackgroundColor') {
				hexColor = this.colorsService.getHexColorFromDashboardColorSet(bgColorId);
			}

			if (hexColor) {
				properties[property.toolkitPropertyName] = { hexColor, isThemeColor: false };
			} else if (themeColors && themeColors.length) {
				const themeColor = themeColors.find(color => color.id === name);
				if (themeColor) {
					properties[property.toolkitPropertyName] = { hexColor: themeColor.value, isThemeColor: true };
				}
			}

			return properties;
		},

		_getColorProperties: function(model) {
			const colorProperties = {};
			const dashboardColorProperties = this._getDashboardColorProperties();
			const keys = Object.keys(dashboardColorProperties);
			keys.forEach(key => {
				colorProperties[key] = dashboardColorProperties[key].hexColor;
			});

			if (model) {
				const contentId = (this.selectedModel && this.selectedModel.id) || model.id;
				const tabContent = this.content.getContent(contentId);
				let changedPageColorPropertyList = this._getChangedPageColorPropertyList(model, tabContent);
				if(changedPageColorPropertyList.length > 0) {
					if(changedPageColorPropertyList.find(property => property.modelPropertyName === 'tabBackgroundColor')) {
						// The page level tab background is set.
						// This should affect the other properties if they are transparent.
						changedPageColorPropertyList = colorPropertiesMapping;
					}
					changedPageColorPropertyList.forEach(property => {
						const pageTabProperty = this._getColorProperty(property, model, tabContent);
						const name = property.toolkitPropertyName;
						if (pageTabProperty[name]) {
							const tabHasValue = !pageTabProperty[name].isThemeColor;
							const dashboardHasValue = dashboardColorProperties[name] && !dashboardColorProperties[name].isThemeColor;
							if (tabHasValue) {
								colorProperties[name] = pageTabProperty[name].hexColor;
							} else if (dashboardHasValue) {
								colorProperties[name] = dashboardColorProperties[name].hexColor;
							} else {
								// Just use the page tab value
								colorProperties[name] = pageTabProperty[name].hexColor;
							}
						}
					});

				}
				// Set the iconColor to the textColor if not defined
				if (!model.get('tabIconColor')) {
					colorProperties['iconColor'] = colorProperties['textColor'];
				}
			}
			this._setDeleteColors(colorProperties);

			return colorProperties;
		},

		_setDeleteColors(properties) {
			let backgroundColor = properties.backgroundColor;
			if(backgroundColor === 'transparent') {
				backgroundColor = this.colorsService.getValueForPropertyOfUIElement('board', 'backgroundColor');
			}
			if(backgroundColor) {
				properties.hoverColor = ColorUtils.invertColor(backgroundColor, 20, 100, true);
				properties.deleteBackgroundColor = ColorUtils.invertColor(backgroundColor, 40, 100, true);
				properties.deleteIconColor = properties.textColor;
			}
		},

		/**
		 * adds the content of the tab in the tabPageHolder or the mounted node
		 * the first container is used when the mounted node is not a valid one: another node was mounted.
		 * we check this with the index: if it does not correspond to the last element of the _renderingMountingNode, it means
		 * the mounted node is not the right one
		 * @param {DOMelement} node - mounted node
		 * @param {string} id - id of the tab
		 * @param {integer} index - index of the mounted operation while showing a tab
		 * @returns {promise} resolved when the content of the tab is added either in the tapPageHolder or the mounted node
		 */
		_renderTabContent(node, id, index) {
			const impl = () => {
				return this.layoutController.getLayoutViewWhenReady(id).then((layoutView) => {
					if (layoutView) {
						const pageContent = this._getTabNode(layoutView.domNode, id);
						if (index + 1 === this._renderingMountingNode[id].length) {
							this._renderingMountingNode[id] = [];
							this._mountedNodesArePopulated[id] = true;
							$(node).append(pageContent);
							this.eventRouter.trigger('tab:tabChanged', { modelId: this.selectedModel.id, isTabWidget: !!this.model.getParent() });
							layoutView.onShow();
							return this._invokeLifeCycleHandlers('tabs.onShow', {
								$el: this.$el,
								model: this.selectedModel
							}).then(() => this._updateHeader());
						} else {
							this.$el.children('.tabPageHolder').append(pageContent);
						}
					}
				});
			};
			return this._ensureDelayedChildIsCreated(id, impl);
		},

		_createTabPanel: function(pageModel, usePageModelForProperties = true) {
			const TabPanel = UI_Toolkit.TabPanel;
			const id = pageModel.id;
			const tabColorProperties = this._getColorProperties(usePageModelForProperties ? pageModel : null);

			const _tabIconObj = pageModel.get('tabIcon');
			let tabIcon = _tabIconObj && _tabIconObj !== 'transparent' ? this._icons.getIcon(_tabIconObj).id : null;

			let properties = {
				id: this._getPanelId(pageModel.id),
				label: pageModel.get('title'),
				icon: tabIcon,
				keepTabContent: true,
				tooltipOrient: 'top'
			};

			_.extend(properties, tabColorProperties);

			/**
			 * when moving tabs the react node is mounted, unmounted and then mounted again; this makes the first node
			 * an invalid one.
			 * in order to make sure we add the rendered tab in the last & correct one, all the _renderTabContent are stacked
			 * and executed in their order and the last invocation adds the layout to the mounted node (see _renderTabContent)
			 * This logic could/should be simplified
			 */
			return React.createElement(TabPanel, properties, [
				React.createElement('div', { ref: (node) => {
					if (!node){
						this._mountedNodesArePopulated[id] = false;
					} else {
						if (!this._renderingMountingNode[id]) {
							this._renderingMountingNode[id] = [];
						}
						if (this._renderingMountingNode[id].length === 0) {
							this._renderingMountingNode[id][0] = this._renderTabContent(node, id, 0);
						} else {
							const nextIndex = this._renderingMountingNode[id].length;
							const previous = this._renderingMountingNode[id][nextIndex-1];
							this._renderingMountingNode[id][nextIndex] = new Promise((resolve) => {
								previous.finally(() => {
									resolve(this._renderTabContent(node, id, nextIndex));
								});
							});
						}
					}
				} })
			]);
		},

		_getFontClass() {
			const fontFamilyClassString = this.content.getPropertyValue('tabFontFamily') || 'ibmplexsans';
			const fontFamilySubClassString = this.content.getPropertyValue('tabFontFamilySubClass') || 'regular';
			const fontFamilyClass = TextEditorConstants.FONT_FAMILY_CHOICES.find(choice => fontFamilyClassString === choice.value);
			let fontClass = fontFamilyClass.cssClass;
			if (fontFamilyClass.subCssClasses && fontFamilyClass.subCssClasses.length > 0) {
				const fontFamilySubClass = fontFamilyClass.subCssClasses.find(choice => fontFamilySubClassString === choice.value);
				if (fontFamilySubClass) {
					fontClass = fontFamilySubClass.cssClass;
				}
			}
			return fontClass;
		},

		_getFontFamilyStyles() {
			const fontClass = this._getFontClass();
			const fontFamilyDiscoveryNodeContainer = document.createElement('div');
			const fontFamilyDiscoveryNode = document.createElement('div');
			fontFamilyDiscoveryNodeContainer.append(fontFamilyDiscoveryNode);
			fontFamilyDiscoveryNodeContainer.classList.add('widget');
			fontFamilyDiscoveryNode.classList.add(fontClass);
			document.body.append(fontFamilyDiscoveryNodeContainer);
			const fontFamilyStyles = ['font-family', 'font-style', 'font-weight'].reduce(
				(prev, curr) => {
					return {
						...prev,
						[curr]: window.getComputedStyle(fontFamilyDiscoveryNode).getPropertyValue(curr)
					};
				},
				{});
			fontFamilyDiscoveryNodeContainer.remove();
			return fontFamilyStyles;
		},

		_updateFontFamily() {
			this.$el.find('.ba-common-tabList').first().css(this._getFontFamilyStyles());
		},

		_renderTabs: function(selectedTabId) {
			// If we this is not a tab level tab then tag the element with the class that will enale to behave like a widget
			// This is currently defined in the fragment but there is a bug in the html templates that will clear them when the fillColor is set on the tab widget
			if (this.content.getContainer()) {
				// widget class will enable the move resizer
				// forcePageFit will force the tab content to resize with the tab control resizes
				this.$el.addClass('widget noRotate forcePageFit');
			}

			const Tabs = UI_Toolkit.Tabs;
			let panels = [],
				panelsMirror = []; //Mirror entry of the tabs that we can compare with for tab changes.

			this.model.items.forEach((item) => {
				const tabPanel = this._createTabPanel(item);
				panels.push(tabPanel);
				panelsMirror.push(tabPanel);
			});

			const tabPosition = this.model.get('tabPosition');
			const tabIconPosition = this.model.get('tabIconPosition');
			const _this = this;
			const content = _this.canvas.getContent(this.model.id);
			const isDropDownHeader = !!(content && content.getPropertyValue('showDropDown'));
			class TabsWidget extends React.Component{
				constructor(props){
					super(props);
					this.state = {
						selectedTabId,
						selectedTab: _this._getPanelId(selectedTabId),
						tabs: panels,
						editable: false,
						vertical: true,
						draggable: false,
						addable: false,
						deleteable: false,
						placement: tabPosition || 'top',
						linePosition: _this._getDefaultLinePostion(tabPosition),
						tabIconPosition,
						isDropDownHeader
					};
					this._tabsMirror = panelsMirror;
					this._icons = _this._icons;
					this.tabListNode = null;
					this.toolBarElementsList = [];
				}

				componentDidUpdate(prevProps, prevState) {
					if (this._updatedTabs) {
						this._updatedTabs = false;
						if (this.state.tabs.length < this._tabsMirror.length) { //Delete
							try {
								_this.deleteView(_this._getModelIdFromPanel(_.difference(this._tabsMirror, this.state.tabs)[0].props.id));
							} catch (e) {
								//if any uncaught excpetion occurs here, the tab will disappear and nothing is useable
								console.log(e);
							}
						}
					}
					this._tabsMirror = this.state.tabs.slice();
					if (prevState.selectedTab !== this.state.selectedTab) {
						_this.showView(_this.model.findModel(_this._getModelIdFromPanel(this.state.selectedTab)));
					}
				}

				componentDidMount() {
					// select the first tab list because we might have a child tab widget
					const tabList = _this.$el.find('.ba-common-tabList').first();
					tabList.on('click.dbTab', _this._tabClick.bind(_this));
					tabList.on('keydown.dbTab', _this._tabKeydown.bind(_this));
					tabList.on('dblclick.dbTab', _this._tabDoubleClick.bind(_this));
					this.tabListNode = tabList;
				}

				componentWillUnmount() {
					// select the first tab list because we might have a child tab widget
					const tabList = _this.$el.find('.ba-common-tabList').first();
					tabList.off('click.dbTab');
					tabList.off('keydown.dbTab');
					tabList.off('dblckick.dbTab');
					this.tabListNode = null;
					this.hasUnmounted = true;
				}

				updateTheme() {
					const tabs = [];
					this.state.tabs.forEach((tab, index) => {
						tabs.push(this._createTabElements(tab, index));
					});
					this.setState({ tabs: tabs });
				}

				editTabLabel(startEdit, model) {
					const isEditable = !!startEdit;
					let $node;
					if (this.tabListNode) {
						$node = $(this.tabListNode);
					} else {
						// Handle child tab widget - use first() to scope to the proper tab list
						$node = _this.$el.find('.ba-common-tabList').first();
					}
					$node.find('[role=tab][aria-selected=true]').focus(); //HACK - Move the focus to the tab. If the focus happens to be on the label when editing is turned on, the re-render may happen before we try to focus.
					this.setState({ editable: isEditable }, () => {
						if (isEditable) {
							let label = $node.find('[contentEditable=true]');
							const panelId = _this._getPanelId(model.id);
							let originalLabel;
							const originalTab = _.find(this.state.tabs, panel => (panelId === panel.props.id));
							if (originalTab) {
								originalLabel = originalTab.props.label;
							}
							const observer = new MutationObserver(() => { //HACK HACK HACK HACK ------- Currently, on the first focus to the editable area, the toolkit tabs are re-rendering, thus blurring the focus immediately. The field needs to be found again and re-focused after the re-render (As there is a new label after this) to actually edit.
								if (!$.contains(_this.$el, label)) {
									observer.disconnect(); //The re-render has happened, so we no longer need this observer.
									label = $node.find('[contentEditable=true]');

									// TEMPORARY CODE - Replace when the tabs provide a callback when editing is complete
									label.on('blur.dashboardtabs', () => {
										label.off('blur.dashboardtabs');
										this.setState({ editable: false }, () => {
											const newTab = _.find(this.state.tabs, panel => (panelId === panel.props.id));
											if (newTab) {
												const newLabel = newTab.props.label;
												if (originalLabel !== newLabel) {
													newTab.props.title = newLabel;
													_this.updateViewLabel(model, newLabel);
												}
											}
										});
									});

									label.focus();
								}
							});
							observer.observe(_this.$el[0], { childList: true, subtree: true }); //Observe changes in descendant nodes of the tabs to catch the re-render caused by the first focus.

							label.focus();
						}
					});
				}

				updateTabLabel(model) {
					let newTabs = [];
					const panelId = _this._getPanelId(model.id);
					for (let i=0; i<this.state.tabs.length; i++) {
						const tab = this.state.tabs[i];
						const title = model.get('title');
						if (tab.props.id == panelId && tab.props.label !== title) {
							newTabs.push(React.cloneElement(tab, {
								label: title,
								title: title
							}));
						} else {
							newTabs.push(tab);
						}
					}
					const old = JSON.stringify(this.state.tabs);
					if (old !== JSON.stringify(newTabs)) {
						this.setState({ tabs: newTabs });
					}
				}

				updateTabIcon(model) {
					let newTabs = [], changeMade = false;
					const panelId = _this._getPanelId(model.id);
					for (let i=0; i<this.state.tabs.length; i++) {
						const tab = this.state.tabs[i];
						const _tabIconObj = model.get('tabIcon');

						let icon;
						if(_tabIconObj && _tabIconObj !== 'transparent') {
							icon = this._icons.getIcon(_tabIconObj).id;
						} else {
							icon = null;
						}

						if(tab.props.id === panelId && tab.props.icon !== icon && !this.translationMode) {
							newTabs.push(React.cloneElement(tab, {
								icon: icon
							}));
							changeMade = true;
						} else {
							newTabs.push(tab);
						}
					}
					if (changeMade) {
						this.setState({ tabs: newTabs });
					}
				}

				/**
				 * @param  {Object} model: the model of a view whose children element need to be translated
				 * @param  {Boolean} shown: when it is true means we want to show the translate icon for this model.
				 * @returns {Int} numberOfChanges: the number of tabs we've updated.
				 */
				updateTranslationIcon(model, shown) {
					const icons = _this.dashboardApi.getFeature('Icons');
					this.translationMode = shown;
					let newTabs = [], changeMade = false, numberOfChanges = 0;
					const panelId = _this._getPanelId(model.id);
					const title = model.get('title');
					for (let i=0; i<this.state.tabs.length; i++) {
						const tab = this.state.tabs[i];
						if (tab.props.id == panelId) {
							if (shown) {
								newTabs.push(React.cloneElement(tab, {
									icon: icons.getIcon('warning').id,
									className: 'translateIcon',
									label: title,
									title
								}));
								changeMade = true;
								numberOfChanges++;
							} else if (tab.props.icon) {
								const tabIcon = model.get('tabIcon');
								newTabs.push(React.cloneElement(tab, {
									icon: tabIcon ? tabIcon.name : undefined,
									className: undefined,
									label: title,
									title
								}));
								changeMade = true;
								numberOfChanges++;
							}
						} else {
							newTabs.push(tab);
						}
					}
					if (changeMade) {
						this.setState({ tabs: newTabs });
					}
					return numberOfChanges;
				}

				_createTabElements(tab, index) {
					const pageModel = _this.model.items[index];
					const colorProperties = _this._getColorProperties(pageModel);
					colorProperties['iconPosition'] = this.state.tabIconPosition;
					return React.cloneElement(tab, colorProperties);
				}

				render() {
					const tabColorProperties = _this._getColorProperties();
					const tabs = [];
					this.state.tabs.forEach((tab, index) => {
						tabs.push(this._createTabElements(tab, index));
					});

					_this._TabsWidget = this;

					const tabsContanerClassNames = cx(
						'ba-theme-default',
						_this._getContentBackgroundProperty(this.state.selectedTabId)
					);

					return React.createElement(
						'div',
						{
							className: tabsContanerClassNames,
							style: {}
						},

						// FIXME: We shouldn't render both "Select" and "Tabs" components when only one will ever be used at a time.
						// Instead create/render the one we use and have logic in the parent React component that knows which to render.
						[
							React.createElement(UI_Toolkit.Select, {
								selected: _this.selectedItem || _this.getSelectedSubViewId(),
								options: _this.model.items.map((item) => {
									return {
										value: item.get('id'),
										label: item.get('title')
									};
								}),
								variant: 'frameless',
								onChange: value => {
									_this.selectedItem = value;
									_this.onSelection({ info: { value: [value] } });
								}
							}),
							React.createElement(Tabs, {
								draggable: this.state.draggable,
								selected: this.state.selectedTab,
								stateTabs: this.state.tabs,
								editable: this.state.editable,
								addable: this.state.addable,
								textDir: BidiUtil.userPreferredTextDir,
								onChange: tabId => {
									const newTabId = _this._getModelIdFromPanel(tabId);
									_this.canvas.selectContent([newTabId]);
								},
								setState: option => { this._updatedTabs = true; this.setState({ tabs: option }); },
								onAddButtonClick: () => _this.onAddClick(),
								onReorder: (newIndex, tabId) => { _this.onReorder(tabs, newIndex, tabId); },
								scrollButtonsBackgroundColor: _this.colorsService.getLayoutBackgroundColorHex(_this.model.get('fillColor')),
								scrollButtonsTextColor: tabColorProperties.textColor,
								placement: this.state.placement,
								linePosition: this.state.linePosition
							}, tabs)
						]
					);

				}
			}
			const DragEnabledTabsWidget = ReactHtml5DnDContext(TabsWidget);

			ReactDOM.render(React.createElement(DragEnabledTabsWidget), this.$el.find('.tabCntr')[0]);
		},

		_getContentBackgroundProperty(tabId) {
			const tabContent = this.content.getContent(tabId);
			if (!tabContent) {
				return '';
			}

			let tabBackgroundColor;
			// If the individual tab color for the tab is transparent, then use the default tab color instead
			const indivTabBgColor = tabContent.getPropertyValue('backgroundColor');
			if (indivTabBgColor && indivTabBgColor !== 'transparent') {
				tabBackgroundColor = indivTabBgColor;
			} else {
				tabBackgroundColor = this.model.get('fillColor');
			}

			if (!tabBackgroundColor) {
				return '';
			}

			return `fill-${tabBackgroundColor}`;
		},

		/**
		 * Fetch the TabPane object given the TabList tab node.
		 */
		_getPanelFromNode: function(tabNode) {
			if (tabNode) {
				// Handle child tab widget - use first() to scope to the proper tab list
				const tabIndex = this.$el.find('.ba-common-tabList').first().find('[role=tab]').index(tabNode);
				if (tabIndex>-1) {
					return this._TabsWidget.state.tabs[tabIndex];
				}
			}
			return null;
		},

		/**
		 * Fetch the model and tab node to send events from the overall tab control to the appropriate tab.
		 * Returns undefined if the event doesn't map to a tab.
		 */
		_getEventModelInfo: function(event) {
			let result = undefined;

			const $target = $(event.target);
			const tabElement = $target.closest('[role=tab]')[0];
			if (tabElement) {
				const tabPanel = this._getPanelFromNode(tabElement);
				if (tabPanel) {
					const panelId = tabPanel.props.id;
					const isSelected = this._TabsWidget.state.selectedTab === panelId;
					const pageModel = this.model.findModel(this._getModelIdFromPanel(panelId));

					result = {
						selected: isSelected,
						node: tabElement,
						model: pageModel,
						editing: () => this._TabsWidget.state.editable
					};
				}
			}
			return result;
		},

		_tabClick: function(event) {
			//this prevents the context bar from appearing when showing the tab toolbar
			let eventChainLocal = new EventChainLocal(event);
			eventChainLocal.setProperty('preventDefaultContextBar', true);
			const eventInfo = this._getEventModelInfo(event);
			if (eventInfo && eventInfo.selected && !eventInfo.editing()) {
				//This click could have triggered a tab delete. To avoid relying on additional internal information about the tabs, add a short delay and then check if the node is still in the DOM before trying to trigger the toolbar.
				setTimeout(() => {
					if ($(eventInfo.node).closest('.ba-common-tabList').length && !eventInfo.editing()) {
						this.showContextMenu(eventInfo.model, eventInfo.node);
					}
				}, 10);
			}
		},

		_tabKeydown: function(event) {
			const eventInfo = this._getEventModelInfo(event);
			if (eventInfo && eventInfo.selected) {
				this._handleKeydown(eventInfo.model, event, eventInfo.node);
			}
		},

		_tabDoubleClick: function(event) {
			const eventInfo = this._getEventModelInfo(event);
			if (eventInfo && eventInfo.selected && !eventInfo.editing()) {
				//Mirror the toolbar show timeout to prevent the toolbar showing from immediately bluring the text field when edit starts. Additionally check that the delete button was not the target of the doubleclick.
				setTimeout(() => {
					if ($(eventInfo.node).closest('.ba-common-tabList').length) {
						this.editViewTitle(eventInfo.model);
					}
				}, 50);
			}
		},

		_attachView: function(/*model*/) {
			//Overridden in authoring.
		},

		onAddClick: function() {
			//Overrridden in authoring.
		},

		onReorder: function(/*tabsList, newIndex, tabId*/) {
			//Overridden in authoring.
		},

		onDeletePrompt: function () {
			const dlg = new ConfirmationDialog({
				title: StringResources.get('tabDeleteDialogTitle'),
				message: StringResources.get('tabDeleteDialogMessage'),
				confirmCallback: this._deleteCallbackFn.bind(this),
				primaryButtonText: StringResources.get('tabDeleteDialogPrimaryButton')
			});
			dlg.render();
		},

		/**
		 * This method to replace the deleteHandler in toolkit when the icon 'X' in old design is clicked
		 */
		_deleteCallbackFn: function() {
			const tabPanelId = this._TabsWidget.state.selectedTab;
			if (tabPanelId) {
				// exclude the selected Tab from list of Tabs
				const newTabs =  this._TabsWidget.state.tabs.filter(tab => tab.props.id !== tabPanelId);
				this._TabsWidget.setState({ tabs: newTabs });

				// delete view to update layouts in boardModel
				this.deleteView(this._getModelIdFromPanel(tabPanelId));
			}
		},

		deleteView: function(/* sViewId */) {
			//Overridden in authoring.
		},

		updateViewLabel: function(/*pageModel, sText*/) {
			//Overridden in authoring.
		},

		getSelectedSubViewId: function() {
			return this.selectedModel && this.selectedModel.id;
		},

		onSelection: function(event) {
			const idList = event.info.value;
			if (event.name === 'change:selections:select' &&
					!idList.find(id => this.model.id === id ||
						this.model.items.find(item => item.getId() === id))) {
				return;
			}

			event.info.value.forEach((id) => {
				const content = this.canvas.getContent(id);
				if (!(content && content.getType() === 'page')) {
					return;
				}

				if(this.selectedModel && this.selectedModel.id !== id) {
					this._updateSelectedModel(this.model.items.find(item => item.getId() === id), true);
				}

				if (this._TabsWidget) {
					this._TabsWidget.setState({ selectedTabId: id, selectedTab: this._getPanelId(id) });
					this._TabsWidget.updateTheme();
				}
			});
		},

		getSelectedSubViewTitle: function() {
			return this.selectedModel.get('title');
		},

		_updateSelectedModel(model, deselectContent = false) {
			if (this.selectedModel) {
				if (deselectContent) {
					this.canvas.deselectContent([this.selectedModel.id]);
				}

				const layoutView = this.layoutController.getLayoutView(this.selectedModel.id);
				if (layoutView) {
					layoutView.onHide();
				}
			}

			this.selectedModel = model;
		},

		showView: function(model) {
			const sTabId = this._getViewId(model);
			if (this._currentViewId === sTabId) {
				return;
			}

			this._currentViewId = sTabId;
			this._updateSelectedModel(model);

			// Handle child tab widget - use first() to scope to the proper tab list
			this.$el.find('.pageTabContent.selected').first().removeClass('selected');
			$('#' + sTabId, this.$el).addClass('selected');

			if (this._mountedNodesArePopulated[model.id]) {
				this.eventRouter.trigger('tab:tabChanged', { modelId: this.selectedModel.id, isTabWidget: !!this.model.getParent()  });
				this.layoutController.getLayoutView(model.id).onShow();
				this._invokeLifeCycleHandlers('tabs.onShow', {
					$el: this.$el,
					model: model
				}).then(() => this._updateHeader());
			}
		},

		// FIXME: Shouldn't be using non-React ways to interact React created/managed elements.
		_updateHeader() {
			const tabTitleElements = this.$el.find('.bx--form-item').first();
			if (!this.model.getParent() || !this.model.get('showDropDown')) {
				this.$el.removeClass('tabsHidden');
				tabTitleElements.addClass('hide');
			} else {
				this.$el.addClass('tabsHidden');
				tabTitleElements.removeClass('hide');
			}
		},

		_updateProperties() {
			this._updateHeader();
			this.eventRouter.trigger('properties:refreshPane');
		},

		_ensureDelayedChildIsCreated(id, callack) {
			if (this._delayedRendering[id]){
				return this.renderChild(id).then(() => {
					return callack();
				});

			} else {
				return callack();
			}
		},

		_getLabelId: function(contentModel) {
			return contentModel.id + '_tablabel';
		},

		_handleKeydown: function(/*model, event, node*/) {
		},

		showContextMenu: function(/*model, node*/) {
			//Overridden in authoring.
		},

		editViewTitle: function(/*model*/) {
			//Overridden in authoring.
		},

		/**
		 * Helper to delegate to this.showView
		 * this double delegation is needed as this.showView may get overridden when authoring
		 * When moving to author mode, event handlers using 'this.showView()' in _attachView() in consume view ctor
		 * need to invoke the overridden showView()
		 *
		 * PL DO NOT REMOVE THIS FUNCTION
		 */
		_showView: function(model) {
			this.showView(model);
		},

		_getViewId: function(contentModel) {
			return contentModel.id + '_tab';
		},

		_invokeLifeCycleHandlers: function(name, payload) {
			return this.dashboardApi.getDashboardSvc('.LifeCycleManager')
				.then(function (LifeCycleManager) {
					return LifeCycleManager.invokeLifeCycleHandlers(name, payload);
				});
		},

		applyTheme: function() {
			this._TabsWidget.updateTheme();
		},

		onTabAdded: function(payload) {
			this._updateHideTabProperty();
			const tabContent = this.canvas.getContent(payload.info.newContentId);
			this._registerContentListener(tabContent);
			const state = tabContent.getFeature('state');
			return state.whenStatusChanges(state.STATUS.RENDERED).then(() => {
				this._TabsWidget.updateTheme();
				this.eventRouter.trigger('properties:refreshPane');
			});
		},

		onTabRemoved: function(payload) {
			this._unregisterContentListener(payload.info.value);
			this._updateHideTabProperty();
			this.eventRouter.trigger('properties:refreshPane');
		},

		_updateHideTabProperty: function() {
			const allowHidingMultipleTabs = this.content.getPropertyValue('allowHidingMultipleTabs');
			if (!allowHidingMultipleTabs) {
				const haveMultipleTabs = this.model.items.length > 1;
				this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'showTabsInPreview', enabled: !haveMultipleTabs });
				if (haveMultipleTabs) {
					this.content.setPropertyValue('showTabsInPreview', true);
				}
			}
		},

		updateHideTabState: function() {
			const checked = !this.model.get('hideTab');
			this.eventRouter.trigger('properties:setValue', { propertyName: 'showTabsInPreview', value: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabPosition', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabIconPosition', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabTextColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabSelectedLineColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabBackgroundColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabRestoreDefaults', enabled: checked });
			this._setTabsVisibility();
		},

		updateTabPosition: function(options) {
			if(this._TabsWidget && options) {
				const tabPosition = options.value || 'top';
				this._TabsWidget.setState({
					placement: tabPosition,
					linePosition: this._getDefaultLinePostion(tabPosition)
				});
				const updatePayload = {
					propertyName: 'tabPosition',
					value: tabPosition
				};
				this.eventRouter.trigger('properties:setValue', updatePayload);
			}
		},

		updateTabIconPosition: function(options) {
			if(this._TabsWidget && options && options.value) {
				const tabIconPosition = options.value;
				this._TabsWidget.setState({
					tabIconPosition
				});
			}
		},

		setInitialEnabledState: function() {
			const checked = !this.model.get('hideTab');
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabPosition', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabIconPosition', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabTextColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabSelectedLineColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabBackgroundColor', enabled: checked });
			this.eventRouter.trigger('properties:updateEnabled', { propertyName: 'tabRestoreDefaults', enabled: checked });
		},

		updateState: function (state) {
			if (!this._TabsWidget.hasUnmounted) {
				//This is the case where in authouring mode and the user decide to close the dahsboard from the switcher

				//Should not be calling consume's view React set state here since the consumer view's destroy unmount the React component
				//Call setState will cause the error in the console:
				//Warning: Can't call setState (or forceUpdate) on an unmounted component. This is a no-op, but it indicates a memory leak in your application. To fix, cancel all subscriptions and asynchronous tasks in the componentWillUnmount method.
				//in TabsWidget (created by DragDropContext(TabsWidget))
				//in DragDropContext(TabsWidget)
				this._TabsWidget.setState(state);
			}
		},

		_onTabColorChange: function (propertyName, propertyValueInfo) {
			// get layout and update CSS
			var oLayout = this.model;
			var data = {
				undoRedoTransactionId: _.uniqueId(`layout_${propertyName}Changed_`)
			};

			const payload = {};
			// Treat transparent as reset to default
			payload[propertyName] = propertyValueInfo.name == 'transparent' ? null : propertyValueInfo.name;
			this.colorsService.prepareForColorModelChange(payload, propertyName);

			oLayout.set(payload, {
				sender: this.senderId,
				payloadData: data
			});
		},

		/**
		 * Find out which one of the 'colorProperitesMapping' items has changed in the model/content and return the ones that changed.
		 */
		_getChangedPageColorPropertyList: function (model, tabContent) {
			return colorPropertiesMapping.filter(property => {
				const modelProperty = model.get(property.modelPropertyName);
				// For tabBackgroundColor we care if the content property "backgroundColor" is set.
				if (property.modelPropertyName === 'tabBackgroundColor' && tabContent) {
					return tabContent.getPropertyValue('backgroundColor') || this.model.get('fillColor') || modelProperty;
				}
				return modelProperty;
			});
		},

		_setTabsVisibility: function() {
			const hideTabs = this.model.get('showDropDown') || !this.content.getPropertyValue('showTabsInPreview') && !this._dashboardState.getUiState().authoring;
			this.$el.toggleClass('tabsHidden', hideTabs);
		}
	});

	return Tab;
});
